import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, UserCheck, FileSpreadsheet, ShieldCheck, Wallet, Percent, TrendingUp,
  RefreshCw, Receipt, IndianRupee, Loader2, Activity, Target,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Cell } from "recharts";
import LoanDashboard from "@/pages/LoanDashboard";

const PERIODS = [
  { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" },
  { value: "week", label: "This Week" }, { value: "month", label: "This Month" },
];

function Metric({ label, value, icon: Icon, tone = "primary", sub, onClick, testId }) {
  const tones = {
    primary: "text-blue-600 bg-blue-50 dark:bg-blue-950/50 dark:text-blue-400",
    emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/50 dark:text-emerald-400",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950/50 dark:text-amber-400",
    rose: "text-rose-600 bg-rose-50 dark:bg-rose-950/50 dark:text-rose-400",
    violet: "text-violet-600 bg-violet-50 dark:bg-violet-950/50 dark:text-violet-400",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left rounded-xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-2xl font-extrabold tracking-tight tabular">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </button>
  );
}

export default function Dashboard() {
  const { user, hasPerm } = useAuth();
  const nav = useNavigate();
  const [period, setPeriod] = useState("month");
  const [view, setView] = useState("insurance");
  const canLoans = hasPerm("loans:view");
  const [m, setM] = useState(null);
  const [team, setTeam] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/dashboard/metrics", { params: { period } }),
      api.get("/dashboard/team-performance").catch(() => ({ data: [] })),
      hasPerm("renewals:view") ? api.get("/renewals/forecast").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]).then(([a, b, c]) => {
      setM(a.data); setTeam(b.data || []); setForecast(c.data);
    }).finally(() => setLoading(false));
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !m) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const chartData = team.slice(0, 6).map((t) => ({ name: t.employee.split(" ")[0], policies: t.policies, leads: t.leads }));

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user?.name?.split(" ")[0]} 👋`}
        subtitle="Live business overview — all figures from actual records"
        icon={Activity}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canLoans && (
              <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" data-testid="business-toggle">
                {["insurance", "all", "loans"].map((v) => (
                  <button key={v} onClick={() => setView(v)} data-testid={`business-toggle-${v}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {v === "all" ? "All" : v}
                  </button>
                ))}
              </div>
            )}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36" data-testid="dashboard-period-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      />

      {view !== "loans" && (<>
      {/* Operational */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        <Metric label="Total Customers" value={m.total_customers} icon={Users} tone="primary" onClick={() => nav("/customers")} testId="dashboard-total-customers-metric" />
        <Metric label="New Leads" value={m.new_leads} icon={Target} tone="violet" onClick={() => nav("/leads")} testId="dashboard-new-leads-metric" />
        <Metric label="Active Policies" value={m.active_policies} icon={ShieldCheck} tone="emerald" onClick={() => nav("/policies")} testId="dashboard-active-policies-metric" />
        <Metric label="Renewals Due (30d)" value={m.renewals_due} icon={RefreshCw} tone="amber" onClick={() => nav("/renewals")} testId="dashboard-renewals-due-metric" />
        <Metric label="Quotes Generated" value={m.quotes_generated} icon={FileSpreadsheet} tone="primary" onClick={() => nav("/quotes")} />
        <Metric label="Policies Booked" value={m.policies_booked} icon={ShieldCheck} tone="primary" onClick={() => nav("/policies")} />
        <Metric label="Conversion Rate" value={`${m.conversion_rate}%`} icon={TrendingUp} tone="emerald" />
        <Metric label="Proposals Pending" value={m.proposals_pending} icon={FileSpreadsheet} tone="amber" onClick={() => nav("/proposals")} />
      </div>

      {/* Financial */}
      <h2 className="text-lg font-bold mt-8 mb-4 flex items-center gap-2"><IndianRupee className="h-5 w-5 text-primary" /> Financial Summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        <Metric label="Business Volume (Premium)" value={INR(m.total_premium)} icon={Wallet} tone="primary" sub="Total premium — not revenue" testId="dashboard-total-premium-metric" />
        <Metric label="Commission Expected" value={INR(m.commission_expected)} icon={Percent} tone="violet" sub="Accrued / gross revenue" />
        <Metric label="Commission Received" value={INR(m.commission_received)} icon={Percent} tone="emerald" sub="Actual received" onClick={() => nav("/commissions")} />
        <Metric label="Commission Pending" value={INR(m.commission_pending)} icon={Percent} tone="amber" onClick={() => nav("/commissions")} />
        <Metric label="Operating Expenses" value={INR(m.expenses)} icon={Receipt} tone="rose" onClick={() => nav("/expenses")} />
        <Metric label="Net Profit" value={INR(m.net_profit)} icon={TrendingUp} tone={m.net_profit >= 0 ? "emerald" : "rose"} sub="Commission received − expenses" testId="dashboard-net-profit-metric" />
        <Metric label="Payments Pending" value={INR(m.payments_pending)} icon={Wallet} tone="amber" onClick={() => nav("/payments")} />
        <Metric label="Renewals Completed" value={m.renewals_completed} icon={RefreshCw} tone="emerald" onClick={() => nav("/renewals")} />
      </div>

      {/* Team + forecast */}
      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Team Performance</h3>
          {chartData.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No team data</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} barGap={6}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="policies" radius={[4, 4, 0, 0]} fill="hsl(var(--chart-1))" name="Policies" />
                <Bar dataKey="leads" radius={[4, 4, 0, 0]} fill="hsl(var(--chart-3))" name="Leads" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-5">
          <h3 className="font-bold mb-4 flex items-center gap-2"><RefreshCw className="h-4 w-4 text-primary" /> Renewal Forecast</h3>
          {forecast && (
            <div className="space-y-3">
              {[["Next 30 days", forecast.due_30], ["Next 60 days", forecast.due_60], ["Next 90 days", forecast.due_90]].map(([lbl, d]) => (
                <div key={lbl} className="rounded-lg border border-border p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{lbl}</span>
                    <span className="text-sm font-bold tabular">{d?.count || 0} policies</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 tabular">Est. premium {INR(d?.expected_premium)} · comm. {INR(d?.expected_commission)}</div>
                </div>
              ))}
              <div className="text-xs text-muted-foreground pt-1">Renewal conversion: <span className="font-bold text-foreground">{forecast.conversion_rate}%</span> · <span className="italic">forecast ≠ confirmed business</span></div>
            </div>
          )}
        </Card>
      </div>
      </>)}
      {canLoans && view !== "insurance" && (
        <div className="mt-10 border-t border-border pt-6">
          <LoanDashboard />
        </div>
      )}
    </div>
  );
}
